import type { ReactNode } from "react";
import { useT } from "../i18n/hooks.ts";
import type { Language } from "../i18n/language.ts";
import type { Theme } from "../model/theme.ts";
import { GearIcon } from "./GearIcon.tsx";
import { MoonIcon } from "./MoonIcon.tsx";
import { DropdownMenu } from "./DropdownMenu.tsx";
import { SunIcon } from "./SunIcon.tsx";

// Language autonyms, never translated — a Spanish speaker wants to read "English", not "Inglés".
const LANGUAGES: { value: Language; label: string }[] = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
];

/**
 * Theme, language and (informational only) which build the running instance
 * was cut from, grouped behind one gear icon. Version is the app's own
 * semantic version straight from `package.json` (`__APP_VERSION__`, see
 * vite.config.ts) — the audit finding: a raw commit hash alone said
 * "developer diagnostic" more than "end-user version". The short SHA is
 * kept as a smaller secondary line for anyone who does still want to trace
 * the build back to a real commit.
 *
 * The open state is controlled from outside (open/onToggle/onClose) —
 * App.tsx owns one shared `settingsMenuOpen` boolean, since this menu's two
 * render sites (Landing's floating use and the workspace topbar) are
 * mutually exclusive.
 */
export function SettingsMenu({
  theme,
  onToggleTheme,
  language,
  onChangeLanguage,
  open,
  onToggle,
  onClose,
  floating = false,
}: {
  theme: Theme;
  onToggleTheme: () => void;
  language: Language;
  onChangeLanguage: (language: Language) => void;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  /** On screens with no top bar (Landing) — wrap this in `.floating-controls`. */
  floating?: boolean;
}) {
  const t = useT();
  const themeOptions: { value: Theme; label: string; icon: ReactNode }[] = [
    { value: "light", label: t("theme.light"), icon: <SunIcon /> },
    { value: "dark", label: t("theme.dark"), icon: <MoonIcon /> },
  ];
  const shortSha = __COMMIT_SHA__ ? __COMMIT_SHA__.slice(0, 7) : null;
  const version = __APP_VERSION__ ? `v${__APP_VERSION__}` : null;

  return (
    <DropdownMenu
      open={open}
      onToggle={onToggle}
      onClose={onClose}
      triggerClassName={floating ? "button-icon" : "button-icon topbar-icon-button"}
      triggerLabel={t("settings.title")}
      triggerIcon={<GearIcon />}
      popoverClassName="popover menu-popover menu-popover-settings"
      closeOnLeave={false}
    >
      <div className="settings-row">
        <span className="settings-row-label">{t("settings.theme")}</span>
        {/* Segmented Sun/Moon control — audit finding: the old icon-only
            button showed the OPPOSITE mode's icon (moon while in light,
            sun while in dark) as a "click me to switch" cue, which read
            as ambiguous without the tooltip. Same segmented pattern as
            the Language row below so the currently-active mode is
            legible at a glance instead of having to be inferred from
            which icon is showing. */}
        <div className="settings-row-options" role="tablist" aria-label={t("settings.theme")}>
          {themeOptions.map((option) => (
            <button
              key={option.value}
              className={option.value === theme ? "button tab-active" : "button"}
              role="tab"
              aria-selected={option.value === theme}
              onClick={() => {
                if (option.value !== theme) onToggleTheme();
              }}
            >
              {option.icon}
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="settings-row">
        <span className="settings-row-label">{t("common.language")}</span>
        <div className="settings-row-options" role="tablist">
          {LANGUAGES.map((l) => (
            <button
              key={l.value}
              className={l.value === language ? "button tab-active" : "button"}
              role="tab"
              aria-selected={l.value === language}
              onClick={() => {
                onChangeLanguage(l.value);
                onClose();
              }}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
      {version && (
        <div className="settings-row">
          <span className="settings-row-label">{t("settings.version")}</span>
          <span className="settings-row-value">
            {version}
            {/* Secondary build reference — only for the developer/QA case
                where a real commit is worth resolving. Muted+smaller so
                the eye reads the semantic version first. */}
            {shortSha && <span className="settings-row-value-build"> · {shortSha}</span>}
          </span>
        </div>
      )}
    </DropdownMenu>
  );
}
